import * as React from 'react';
import classnames from 'classnames';
import { sortBy } from 'lodash';

import ProfileEntityAvatar from 'components/entity/entity-avatar/ProfileEntityAvatar';
import ProfileEntityName from 'components/entity/entity-name/ProfileEntityName';
import SVGDynamicIcon from '../svg-dynamic-icon/SVGDynamicIcon';
import InfluencerSelectorSearch from './InfluencerSelectorSearch';

import { doesMatchSearchQuery } from 'utils/influencers';
import { __t, applyTranslations } from 'utils/language';

import type { InfluencerSelector as InfluencerSelectorAlias, SVGDynamicIconCustomColor } from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./influencer-selector.i18n.json'));

const SCROLL_STEP: number = 80; // value should match `width` of `.influencer-selector__item` class

type Direction = 'prev' | 'next';

type Props = {
  influencerProfiles: Array<InfluencerSelectorAlias>,
  validInfluencerProfileIds?: Array<number>,
  selectedInfluencerProfileId: number | undefined | null,
  shouldSort?: boolean,
  showPlaceholder?: boolean,
  scrollType?: 'single' | 'visible',
  onSelect: (influencerProfileId?: number | null) => void,
  resetButton?: {
    label: string,
    icon: string
  },
  showSearch?: boolean
};

type State = {
  scrollPosition: number,
  canScroll: {
    left: boolean,
    right: boolean
  },
  diff: number,
  searchQuery: string
};

type ArrowDirection = 'left' | 'right';

class InfluencerSelector extends React.Component<Props, State> {
  static defaultProps = {
    shouldSort: true,
    showPlaceholder: true,
    scrollType: 'visible'
  };

  trayContent: HTMLDivElement | undefined | null = null;

  trayList: HTMLUListElement | undefined | null = null;

  state = {
    scrollPosition: 0,
    canScroll: {
      left: false,
      right: false
    },
    diff: 0,
    searchQuery: ''
  };

  componentDidMount(): void {
    this.calcScroll(0);
    if (typeof window === 'object' && window.addEventListener) { // it's for Jest
      window.addEventListener('resize', this.handleResize);
    }
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.props.influencerProfiles.length !== prevProps.influencerProfiles.length) {
      this.calcScroll(0);
    }
  }

  componentWillUnmount(): void {
    if (typeof window === 'object' && window.removeEventListener) { // it's for Jest
      window.removeEventListener('resize', this.handleResize);
    }
  }

  getUpdatedPosition(
    position: number,
    direction: Direction | undefined | null,
    trayWidth: number
  ): number {
    const { scrollType } = this.props;

    if (direction && scrollType === 'visible' && position > 0) {
      const amount = Math.floor(trayWidth / SCROLL_STEP) * SCROLL_STEP;
      let updatedPosition;

      if (direction === 'prev') {
        updatedPosition = (position - amount) + SCROLL_STEP;
      } else {
        updatedPosition = (position + amount) - SCROLL_STEP;
      }

      return updatedPosition;
    }

    return position;
  }

  calcScroll(position: number = 0, direction?: Direction): void {
    if (this.trayContent && this.trayList) {
      const maxWidth = this.trayList.clientWidth;
      const width = this.trayContent.clientWidth;
      const diff = Math.max(maxWidth - width, 0);
      const isScrollNeeded = maxWidth !== width;
      const updatedPosition = this.getUpdatedPosition(position, direction, width);

      this.setState({
        scrollPosition: updatedPosition,
        canScroll: {
          left: isScrollNeeded && updatedPosition > 0,
          right: isScrollNeeded && (updatedPosition < diff)
        },
        diff
      });

      if (this.trayList && this.trayList.style) {
        this.trayList.style.transform = `translateX(${-updatedPosition}px)`;
      }
    }
  }

  handleScrollLeft = (): void => {
    this.calcScroll(Math.max(this.state.scrollPosition - SCROLL_STEP, 0), 'prev');
  };

  handleScrollRight = (): void => {
    this.calcScroll(Math.min(this.state.scrollPosition + SCROLL_STEP, this.state.diff), 'next');
  };

  handleResize = (): void => {
    this.calcScroll(0);
  };

  handleSelect = (event: SyntheticEvent<HTMLLIElement>): void => {
    const { onSelect } = this.props;
    const influencerProfileId = Number(event.currentTarget.dataset.profileid);

    onSelect(influencerProfileId);
  };

  handleSearch = (searchQuery: string): void => {
    this.setState({
      searchQuery
    });
    this.calcScroll(0);
  };

  handleReset = (): void => {
    const { onSelect } = this.props;

    onSelect(null);
  };

  renderArrow(direction: ArrowDirection, isDisabled: boolean): React.ReactNode {
    const arrowClasses = classnames({
      'influencer-selector__tray-arrow': true,
      'influencer-selector__tray-arrow--disabled': isDisabled
    });

    const handleScroll = direction === 'left' ? this.handleScrollLeft : this.handleScrollRight;

    return (
      <div
        className={arrowClasses}
        onClick={!isDisabled ? handleScroll : undefined}
      >
        <SVGDynamicIcon
          name={`chevron-${direction}-custom-fg`}
          width={8}
          height={13}
        />
      </div>
    );
  }

  renderPlaceholder(): React.ReactNode {
    return (
      <li className="influencer-selector__placeholder">
        {__t('influencer_selector.placeholder')}
      </li>
    );
  }

  renderItem = (influencerProfile: InfluencerSelectorAlias): React.ReactNode | undefined | null => {
    const { selectedInfluencerProfileId, validInfluencerProfileIds } = this.props;

    if (!influencerProfile || !influencerProfile.id) {
      return null;
    }

    const isValid = validInfluencerProfileIds && validInfluencerProfileIds.includes(influencerProfile.id);

    const itemClasses = classnames({
      'influencer-selector__item': true,
      'influencer-selector__item--selected': influencerProfile.id === selectedInfluencerProfileId,
      'influencer-selector__item--valid': validInfluencerProfileIds && isValid,
      'influencer-selector__item--invalid': validInfluencerProfileIds && !isValid
    });

    const icon: {
      name: string,
      color: SVGDynamicIconCustomColor
    } = {
      name: isValid ? 'checkmark-inside-circle-white-bg-custom-fg' : 'exclamation-inside-circle-white-bg-custom-fg',
      color: isValid ? 'primary' : 'red'
    };

    return (
      <li
        key={`influencer-selector-item-${influencerProfile.id}`}
        className={itemClasses}
        data-profileid={influencerProfile.id}
        onClick={this.handleSelect}
      >
        <div className="influencer-selector__item-image">
          <ProfileEntityAvatar
            disabled
            profile={influencerProfile}
            customSize={{ width: 48, height: 48 }}
            bordered
            form="round"
          />
          {
            validInfluencerProfileIds && (
              <div className="influencer-selector__item-badge">
                <SVGDynamicIcon
                  name={icon.name}
                  width={18}
                  height={18}
                  customColor={icon.color}
                />
              </div>
            )
          }
        </div>
        <ProfileEntityName
          disabled
          profile={influencerProfile}
          className="influencer-selector__item-name"
        />
      </li>
    );
  };

  renderResetButton = (): JSX.Element | null => {
    const { selectedInfluencerProfileId, resetButton } = this.props;

    const itemClasses = classnames({
      'influencer-selector__item': true,
      'influencer-selector__item--reset': true,
      'influencer-selector__item--selected': !selectedInfluencerProfileId
    });

    if (!resetButton) {
      return null;
    }

    return (
      <div
        className={itemClasses}
        onClick={this.handleReset}
      >
        <div className="influencer-selector__item-image influencer-selector__item-image--reset">
          <SVGDynamicIcon
            name={resetButton.icon}
            width={48}
            height={48}
            customColor="primary"
          />
        </div>
        <div className="influencer-selector__item-name influencer-selector__item-name--reset text-overflow-ellipsis">
          {resetButton.label}
        </div>
      </div>
    );
  };

  render(): React.ReactNode {
    const { resetButton, shouldSort, showPlaceholder, showSearch, influencerProfiles } = this.props;
    const { canScroll, searchQuery } = this.state;

    if (!showPlaceholder && influencerProfiles.length === 0) {
      return null;
    }

    let profiles: Array<InfluencerSelectorAlias> = shouldSort
      ? sortBy(influencerProfiles, 'name')
      : influencerProfiles;

    profiles = profiles.filter(item => !searchQuery || doesMatchSearchQuery(searchQuery, item));

    const listClasses = classnames({
      'influencer-selector__list': true,
      'influencer-selector__list--empty': profiles.length === 0
    });

    const searchClasses = resetButton ? 'influencer-selector__search--padding' : null;

    return (
      <section className="influencer-selector">
        {
          showSearch && (
            <InfluencerSelectorSearch
              searchQuery={searchQuery}
              onSearch={this.handleSearch}
              additionalClasses={searchClasses}
            />
          )
        }
        {
          resetButton && (
            <div className="influencer-selector__reset">
              {this.renderResetButton()}
            </div>
          )
        }

        <div className="influencer-selector__tray">
          {this.renderArrow('left', !canScroll.left)}
          <div
            className="influencer-selector__tray-content"
            ref={ref => { this.trayContent = ref; }}
          >
            <ul
              className={listClasses}
              ref={ref => { this.trayList = ref; }}
            >
              {
                profiles.map(this.renderItem)
              }
              {
                showPlaceholder && profiles.length === 0 &&
                  this.renderPlaceholder()
              }
            </ul>
          </div>
          {this.renderArrow('right', !canScroll.right)}
        </div>
      </section>
    );
  }
}

export default InfluencerSelector;
